/**
 * Shared geometry, materials, and constants for screen-space-scaled
 * edit mode dots (vertex dots, face dots, curve control points, handles).
 *
 * Used by EditModeRenderer and CurveEditOverlay to avoid duplication.
 *
 * Occlusion: back-facing dots are culled in the vertex shader via a
 * per-instance `instanceNormal` attribute and a view-space dot-product
 * test (Blender-style geometric culling, distance-independent).
 *
 * Performance: billboard scale is driven by a shared GPU uniform
 * (`dotBaseScale`) and per-instance attributes, so camera zoom only
 * updates a single float — no per-instance matrix rebuilds needed.
 */

import {
  abs,
  attribute,
  cameraProjectionMatrix,
  cameraViewMatrix,
  Discard,
  dot,
  Fn,
  float,
  length,
  mat3,
  min,
  mix,
  modelNormalMatrix,
  modelViewMatrix,
  normalize,
  positionGeometry,
  positionLocal,
  smoothstep,
  uniform,
  uv,
  vec3,
  vec4,
} from "three/tsl";
import type { Node } from "three/webgpu";
import * as THREE from "three/webgpu";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Visual dot geometry radius in world units — base size before scaling. */
export const VERTEX_RADIUS = 1;
/** Hit sphere radius — larger for comfortable touch/click targeting. */
export const VERTEX_HIT_RADIUS = 8;
/** Desired visual vertex size in screen pixels. */
export const VERTEX_SCREEN_PX = 4;
/** Desired hit target size in screen pixels. */
export const HIT_SCREEN_PX = 12;

// ---------------------------------------------------------------------------
// Blender-style clip-space overlay depth bias
// ---------------------------------------------------------------------------
// Tiered NDC Z offsets applied in vertex shaders to prevent z-fighting
// between coplanar overlay layers and the mesh surface. Each tier pushes
// geometry toward the camera in clip space by `bias * abs(w)`, which is
// perspective-correct (Blender overlay_edit_mesh_lib.glsl technique).
// Higher values = closer to camera = drawn on top.

/** Clip-space bias for face highlight overlays (smallest — just off the surface). */
export const OVERLAY_BIAS_FACE = 0.00004;
/** Clip-space bias for edge overlays (mid-tier). */
export const OVERLAY_BIAS_EDGE = 0.00008;
/** Clip-space bias for vertex / face-dot overlays (largest — always on top). */
export const OVERLAY_BIAS_VERT = 0.00012;

/**
 * Apply perspective-correct clip-space depth bias to a vec4 clip position.
 * Subtracts `bias * abs(w)` from clip Z, pushing the fragment toward the
 * camera without affecting screen-space XY.
 */
export function applyOverlayBias(clip: Node, bias: number): Node {
  const c = vec4(clip).toVar("biasedClip");
  c.z.subAssign(float(bias).mul(abs(c.w)));
  return c;
}

// ---------------------------------------------------------------------------
// Shared geometries
// ---------------------------------------------------------------------------

/** Hit sphere — invisible, keeps 3D volume for raycasting. */
export const HIT_SPHERE = new THREE.SphereGeometry(VERTEX_HIT_RADIUS, 8, 6);

/**
 * Create a per-InstancedMesh dot geometry with the required per-instance
 * attributes. Each InstancedMesh needs its own geometry copy because the
 * per-instance attributes (`instanceScaleCap`, `instanceSizeFactor`) differ.
 */
export function createDotGeometry(maxInstances: number): THREE.PlaneGeometry {
  const geo = new THREE.PlaneGeometry(VERTEX_RADIUS * 2.2, VERTEX_RADIUS * 2.2);
  // Per-instance scale cap (max allowed world-space scale, e.g. from vertex
  // neighbor distance). Default = very large (effectively uncapped).
  const caps = new Float32Array(maxInstances);
  caps.fill(1e6);
  geo.setAttribute(
    "instanceScaleCap",
    new THREE.InstancedBufferAttribute(caps, 1),
  );
  // Per-instance size multiplier (e.g. 1.5× for control points vs 1.0
  // for handles). Default = 1.0.
  const factors = new Float32Array(maxInstances);
  factors.fill(1.0);
  geo.setAttribute(
    "instanceSizeFactor",
    new THREE.InstancedBufferAttribute(factors, 1),
  );
  // Per-instance color (vec3, linear sRGB). Default = dark gray (#444444).
  // Stored on the geometry so the attribute exists when the shader first
  // compiles — mesh.setColorAt() lazily creates a mesh-level attribute
  // which can miss the initial pipeline compilation in WebGPU.
  const colors = new Float32Array(maxInstances * 3);
  const _def = new THREE.Color("#444444"); // linear sRGB
  for (let i = 0; i < maxInstances; i++) {
    colors[i * 3] = _def.r;
    colors[i * 3 + 1] = _def.g;
    colors[i * 3 + 2] = _def.b;
  }
  geo.setAttribute("dotColor", new THREE.InstancedBufferAttribute(colors, 3));
  // Per-instance surface normal (vec3) for back-face culling. Default = zero
  // vector, which disables culling (dot product with any view dir ≈ 0, treated
  // as front-facing). Components that know face normals populate this.
  const norms = new Float32Array(maxInstances * 3); // zero-initialized
  geo.setAttribute(
    "instanceNormal",
    new THREE.InstancedBufferAttribute(norms, 3),
  );
  return geo;
}

// ---------------------------------------------------------------------------
// GPU uniform for base scale — updated once per frame, O(1)
// ---------------------------------------------------------------------------

/**
 * Shared base-scale uniform. All dot materials read this to determine
 * billboard size. The active edit component updates `dotBaseScale.value`
 * each frame based on camera distance and viewport size.
 */
export const dotBaseScale = uniform(float(1));

/**
 * Shared uniform controlling back-face culling for dots. Set to 1.0 to
 * disable culling (x-ray mode), 0.0 to enable it (normal mode). Updated
 * by edit-mode components when the xray state changes.
 */
export const dotXray = uniform(float(0));

// ---------------------------------------------------------------------------
// Billboard circle material for vertex/face/curve dots
// ---------------------------------------------------------------------------

/**
 * Billboard circle material for edit mode dots.
 *
 * Vertex: rotates each instance's quad to face the camera (billboard).
 *   Scale comes from the shared `dotBaseScale` uniform combined with
 *   per-instance cap and size-factor attributes — so camera zoom
 *   never triggers per-instance matrix rebuilds.
 * Fragment: discards pixels outside the unit circle for a clean disc.
 */
const dotMat = new THREE.MeshBasicNodeMaterial({
  transparent: true,
  depthTest: true,
  depthWrite: false,
  side: THREE.DoubleSide,
});

// -- Vertex: billboard positioning via vertexNode ----------------------------
// We use vertexNode (not positionNode) because billboard math operates in
// view space and returns clip-space vec4 directly. positionNode is forced
// to vec3 by Three.js and then has MVP applied on top, which would
// double-project the coordinates.

dotMat.vertexNode = Fn(() => {
  // Instance matrices are translation-only (scale=1), so the instance
  // center in local space = positionLocal - positionGeometry (exact).
  const center = positionLocal.sub(positionGeometry);

  // Transform center to view space. mat4 * vec3 auto-promotes with w=1
  // (same pattern as Three.js internal: modelViewMatrix.mul(positionLocal)).
  const viewCenter = modelViewMatrix.mul(center).toVar("viewCenter");

  // ---- Back-face culling (Blender-style) --------------------------------
  // If the per-instance surface normal faces away from the camera, collapse
  // the vertex to a degenerate point so the GPU clips it. This is a
  // geometric test, distance-independent — no depth buffer involved.
  const instNormal = attribute("instanceNormal", "vec3" as const);
  const normalLen = length(instNormal);
  // Transform normal to view space in two steps:
  //  1. modelNormalMatrix (inverse transpose of model 3×3) — correct
  //     for any scale, including non-uniform.
  //  2. mat3(cameraViewMatrix) (rotation-only, orthonormal) — no
  //     inverse transpose needed for rigid-body transforms.
  // Both the resulting normal and viewDir are in view space.
  const worldNormal = modelNormalMatrix.mul(instNormal);
  const viewNormal = normalize(mat3(cameraViewMatrix).mul(worldNormal));
  // viewCenter.xyz is the instance position in view space; its direction
  // from the origin IS the view direction for that point.
  const viewDir = normalize(vec3(viewCenter.x, viewCenter.y, viewCenter.z));
  const facing = dot(viewNormal, viewDir);
  // facing > 0 means the normal points away from the camera (back-facing).
  // When normalLen ≈ 0 (default zero-vector) or xray is on, skip the test —
  // treat as front-facing so existing call-sites without normals still work.
  const cullBackface = facing
    .greaterThan(0.0)
    .and(normalLen.greaterThan(0.001))
    .and(dotXray.lessThan(0.5));

  // Compute effective billboard scale from the GPU uniform and per-instance
  // attributes. This avoids per-instance matrix rebuilds on camera zoom.
  const sizeFactor = attribute("instanceSizeFactor", "float" as const);
  const cap = attribute("instanceScaleCap", "float" as const);
  const scale = min(dotBaseScale.mul(sizeFactor), cap);

  // Billboard: offset from center in view-space XY by the scaled
  // geometry vertex (screen-aligned in view space by definition).
  viewCenter.x.addAssign(positionGeometry.x.mul(scale));
  viewCenter.y.addAssign(positionGeometry.y.mul(scale));

  // Project to clip space and apply overlay depth bias so dots always
  // render on top of edges and face highlights without z-fighting.
  const clip = vec4(
    applyOverlayBias(cameraProjectionMatrix.mul(viewCenter), OVERLAY_BIAS_VERT),
  ).toVar("clipPos");

  // Degenerate back-facing instances to a point behind the camera so
  // the GPU clips them entirely — no fragment shader cost.
  clip.z.assign(cullBackface.select(float(-2.0).mul(clip.w), clip.z));
  return clip;
})();

// -- Fragment: circular disc with anti-aliased edge -------------------------

// Per-instance color from the geometry-level InstancedBufferAttribute.
const vColor = attribute("dotColor", "vec3" as const);

dotMat.colorNode = Fn(() => {
  // UV goes 0..1; map to centered coords scaled to quad oversize factor
  const centered = uv().sub(0.5).mul(2.2);
  const dist = length(centered);

  // Discard outside unit circle
  Discard(dist.greaterThan(1.0));

  // Add a subtle contrast rim so dots stay legible against both light and
  // dark backgrounds without needing a second draw pass.
  const luma = dot(vColor, vec3(0.299, 0.587, 0.114));
  const outlineColor = mix(
    vec3(1.0, 1.0, 1.0),
    vec3(0.0, 0.0, 0.0),
    smoothstep(0.45, 0.65, luma),
  );
  const outlineMix = smoothstep(0.72, 0.9, dist).mul(0.38);

  return mix(vColor, outlineColor, outlineMix);
})();

dotMat.opacityNode = Fn(() => {
  const centered = uv().sub(0.5).mul(2.2);
  const dist = length(centered);

  Discard(dist.greaterThan(1.0));

  // Crisp anti-aliased edge over last 5% of radius
  return float(1.0).sub(smoothstep(0.92, 1.0, dist));
})();

export const dotMaterial = dotMat;

// ---------------------------------------------------------------------------
// Shared overlay materials — edges and face highlights with clip-space bias
// ---------------------------------------------------------------------------

/**
 * Create a LineBasicNodeMaterial with clip-space overlay bias applied in the
 * vertex shader. Used for all edit-mode edge overlays (mesh edges, selection
 * highlight edges, rail lines).
 */
export function createOverlayLineMaterial(opts: {
  color: string;
  opacity: number;
  depthTest: boolean;
  bias?: number;
}): THREE.LineBasicNodeMaterial {
  const mat = new THREE.LineBasicNodeMaterial({
    color: opts.color,
    transparent: true,
    opacity: opts.opacity,
    depthTest: opts.depthTest,
  });
  const bias = opts.bias ?? OVERLAY_BIAS_EDGE;
  mat.vertexNode = Fn(() => {
    const clip = cameraProjectionMatrix.mul(modelViewMatrix.mul(positionLocal));
    return applyOverlayBias(clip, bias);
  })();
  return mat;
}

/**
 * Create a MeshBasicNodeMaterial with clip-space overlay bias applied in the
 * vertex shader. Used for face highlight overlays that are coplanar with the
 * mesh surface.
 */
export function createOverlayFaceMaterial(opts: {
  color: string;
  opacity: number;
  depthTest: boolean;
  bias?: number;
  side?: THREE.Side;
}): THREE.MeshBasicNodeMaterial {
  const mat = new THREE.MeshBasicNodeMaterial({
    color: opts.color,
    transparent: true,
    opacity: opts.opacity,
    side: opts.side ?? THREE.FrontSide,
    depthTest: opts.depthTest,
    depthWrite: false,
  });
  const bias = opts.bias ?? OVERLAY_BIAS_FACE;
  mat.vertexNode = Fn(() => {
    const clip = cameraProjectionMatrix.mul(modelViewMatrix.mul(positionLocal));
    return applyOverlayBias(clip, bias);
  })();
  return mat;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Reusable Object3D for building instance matrices without allocation. */
export const _dummy = new THREE.Object3D();

/**
 * Bulk-write per-instance normals into the geometry-level `instanceNormal`
 * attribute.  `normals` is a flat Float32Array of vec3s (3 floats per
 * instance, `count` instances).  Uses typed-array set() for speed.
 */
export function setDotNormals(
  geo: THREE.BufferGeometry,
  normals: Float32Array,
  count: number,
): void {
  const attr = geo.getAttribute(
    "instanceNormal",
  ) as THREE.InstancedBufferAttribute;
  if (!attr) return;
  (attr.array as Float32Array).set(normals.subarray(0, count * 3));
  attr.needsUpdate = true;
}

/**
 * Write a THREE.Color into the geometry-level `dotColor` attribute at the
 * given instance index.  Call `attr.needsUpdate = true` after the batch.
 */
export function setDotColor(
  geo: THREE.BufferGeometry,
  index: number,
  color: THREE.Color,
): void {
  const attr = geo.getAttribute("dotColor") as THREE.InstancedBufferAttribute;
  attr.setXYZ(index, color.r, color.g, color.b);
}
